import { Building2, User } from "lucide-react";
import { Field } from "@/components/portal/PortalModal";
import { IN_STATES, LANGUAGES, COUNTRIES } from "@/config/regions";
import { ILLUSTRATIVE_PLANS, formatMoney } from "@/config/plans";

export const StepType = ({ f, set }) => (
  <>
    <p>Choose how you work. Both use the <b>same canonical multi-tenant platform</b>; only onboarding and entitlements differ.</p>
    <div className="optionrow" role="radiogroup" aria-label="Customer type">
      {[["COMPANY", Building2, "Event Planning Company", "Agency, studio, multi-user team or enterprise organizer."], ["INDIVIDUAL", User, "Freelance Event Planner", "Independent professional with a simpler workspace."]].map(([v, Icon, t, d]) => (
        <button type="button" key={v} role="radio" aria-checked={f.type === v} className={`option ${f.type === v ? "selected" : ""}`} onClick={() => set("type", v)} data-testid={`onboard-type-${v.toLowerCase()}`}>
          <Icon className="ic lg" /><b>{t}</b><span>{d}</span>
        </button>
      ))}
    </div>
    <p className="fineprint" style={{ marginTop: 12 }}>A freelancer can later grow into a company within the same tenant, keeping events and customer records. This depends on platform support (pending verification).</p>
  </>
);

export const StepProfile = ({ f, set, errors }) => (
  <div className="formgrid">
    <Field id="ob-name" label={f.type === "INDIVIDUAL" ? "Professional / brand name *" : "Company / agency name *"} error={errors.name}>
      <input id="ob-name" value={f.name} onChange={(e) => set("name", e.target.value)} maxLength={80} placeholder={f.type === "INDIVIDUAL" ? "e.g. Priya Sharma Events" : "e.g. Hyderabad Celebrations"} data-testid="onboard-name-input" />
    </Field>
    <Field id="ob-contact" label="Your full name *" error={errors.contact}>
      <input id="ob-contact" value={f.contact} onChange={(e) => set("contact", e.target.value)} maxLength={80} data-testid="onboard-contact-input" />
    </Field>
    <Field id="ob-email" label="Work email *" error={errors.email}>
      <input id="ob-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} maxLength={120} data-testid="onboard-email-input" />
    </Field>
    <Field id="ob-mobile" label="Mobile (+91) *" error={errors.mobile}>
      <input id="ob-mobile" inputMode="numeric" value={f.mobile} onChange={(e) => set("mobile", e.target.value)} maxLength={10} placeholder="10 digits" data-testid="onboard-mobile-input" />
    </Field>
    {f.type === "COMPANY" && (
      <Field id="ob-team" label="Team size">
        <select id="ob-team" value={f.team} onChange={(e) => set("team", e.target.value)} data-testid="onboard-team-select">{["2-5", "6-20", "21-100", "100+"].map((x) => <option key={x}>{x}</option>)}</select>
      </Field>
    )}
  </div>
);

export const StepLocation = ({ f, set, errors }) => (
  <div className="formgrid">
    <Field id="ob-country" label="Country" hint="UAE opens after India commercialization.">
      <select id="ob-country" value="IN" disabled data-testid="onboard-country-select">{COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}{c.status === "FUTURE" ? " (coming later)" : ""}</option>)}</select>
    </Field>
    <Field id="ob-state" label="State *">
      <select id="ob-state" value={f.state} onChange={(e) => set("state", e.target.value)} data-testid="onboard-state-select">{IN_STATES.map((s) => <option key={s.code} value={s.code}>{s.name}{s.wave === 1 ? " · Wave 1" : ""}</option>)}</select>
    </Field>
    <Field id="ob-city" label="City *" error={errors.city}>
      <input id="ob-city" value={f.city} onChange={(e) => set("city", e.target.value)} maxLength={60} placeholder="e.g. Hyderabad" data-testid="onboard-city-input" />
    </Field>
    <Field id="ob-lang" label="Preferred language">
      <select id="ob-lang" value={f.language} onChange={(e) => set("language", e.target.value)} data-testid="onboard-language-select">{LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.english} · {l.label}</option>)}</select>
    </Field>
  </div>
);

export const StepPlan = ({ f, set }) => (
  <>
    <div className="billing" style={{ width: "fit-content", marginBottom: 12 }} role="group" aria-label="Billing period">
      {["monthly", "annual"].map((b) => <button type="button" key={b} className={f.billing === b ? "active" : ""} onClick={() => set("billing", b)} data-testid={`onboard-billing-${b}`}>{b === "monthly" ? "Monthly" : "Annual"}</button>)}
    </div>
    <div className="optionrow">
      {ILLUSTRATIVE_PLANS.filter((p) => p.audience === f.type).map((p) => {
        const v = f.billing === "annual" ? p.annual : p.monthly;
        return (
          <button type="button" key={p.code} className={`option ${f.plan === p.code ? "selected" : ""}`} onClick={() => set("plan", p.code)} data-testid={`onboard-plan-${p.code.toLowerCase()}`}>
            <b>{p.name}</b><span>{v == null ? "Custom pricing" : `${formatMoney(v)} /mo · illustrative`}</span>
          </button>
        );
      })}
    </div>
    <p className="fineprint" style={{ marginTop: 10 }}>No payment is taken. Trials, limits and activation follow the platform entitlement configuration.</p>
  </>
);

export const StepConsent = ({ f, set, errors }) => (
  <>
    {[["terms", "I accept the Ayndri pilot terms of service. *"], ["privacy", "I consent to processing of my personal data for onboarding, as per India's DPDP Act 2023. *"], ["updates", "Send me product updates (optional)."]].map(([k, label]) => (
      <label key={k} className="checkrow"><input type="checkbox" checked={f.consents[k]} onChange={(e) => set("consents", { ...f.consents, [k]: e.target.checked })} data-testid={`onboard-consent-${k}`} /> {label}</label>
    ))}
    {errors.consents && <small className="err" data-testid="onboard-consent-error">{errors.consents}</small>}
    <div className="notice" style={{ marginTop: 12 }}>Email and mobile verification (OTP) is handled by the existing identity service. This portal does not issue or check OTPs.</div>
  </>
);
